/**
 * Foundry → Connections (D441): the Microsoft Foundry places the organisation has approved. Approving a connection is what
 * lets people use its projects; the organisation's Foundry policy lists exactly these and is rewritten whenever one is
 * added, changed or removed.
 */

import type { FoundryConnection } from '@aioe/shared';
import { Cloud, CircleQuestion, Layers3Diagonal, Server } from '@gravity-ui/icons';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { foundryRoutes, problemText } from '../../api/client.ts';
import { timeAgo, useLoad } from '../../hooks.ts';
import { Button, ConfirmDialog, Empty, ErrorNote, ListGroup, ListRow, PageColumn, PageHeader, Stat } from '../ui.tsx';
import { Explainer } from '../ui/Layout.tsx';
import { RowMenu } from '../ui/RowMenu.tsx';
import { ConnectionForm } from './ConnectionForm.tsx';
import { KIND_WORDS, plural, projectCount, runsAs } from './foundryWords.ts';
import { TONE } from './tone.ts';

export function ConnectionsPanel({ mayEdit }: { mayEdit: boolean }) {
  const list = useLoad(() => foundryRoutes.connections(), [], 60_000);
  // The count of requests waiting is the one figure here that needs a person: it points at the next place.
  const requests = useLoad(() => foundryRoutes.requests().catch(() => undefined), [], 60_000);
  const [form, setForm] = useState<{ connection?: FoundryConnection }>();
  const [removing, setRemoving] = useState<FoundryConnection>();
  const [error, setError] = useState<string>();
  const connections = list.data?.connections;
  const waiting = requests.data?.requests.filter((q) => q.state === 'pending') ?? [];
  const projects = (connections ?? []).reduce((n, c) => n + projectCount(c), 0);
  const latest = (connections ?? []).map((c) => c.updatedAt).sort().at(-1);

  return (
    <PageColumn width="data">
      <PageHeader
        tone={TONE}
        icon={<Cloud />}
        title="Foundry connections"
        subtitle="The Microsoft Foundry places people may work with, and whose sign-in the work runs as."
        actions={
          mayEdit ? (
            <Button size="sm" variant="primary" onPress={() => setForm({})}>
              Add connection
            </Button>
          ) : undefined
        }
      />
      <Explainer id="foundry-connections" tone={TONE} icon={<Cloud />} title="How connections work" summary="Approving a connection is what lets people use its projects">
        <p>Foundry is where an organisation keeps its models and agents on Azure. A person can only work with a Foundry project when the organisation has approved the connection that holds it: its directory, its projects, the model deployments and tools in each.</p>
        <p>The organisation’s Foundry policy lists exactly the connections here. Adding, changing or removing one rewrites that list for every workbench, so there is nothing to keep in step by hand.</p>
        <p>When someone needs a project that is not listed, they ask, and the request waits under Requests for you to approve or decline.</p>
      </Explainer>
      <ErrorNote error={list.error ?? error} />
      <div className="art-stats">
        <Stat tone={TONE} icon={<Cloud />} label="Connections" value={connections?.length} detail={connections ? (latest ? `Last changed ${timeAgo(latest)}` : 'None approved yet') : ''} />
        <Stat tone={TONE} icon={<Layers3Diagonal />} label="Places approved" value={connections ? projects : undefined} detail="Projects and clusters people may use" />
        <Stat tone={TONE} icon={<CircleQuestion />} label="Requests waiting" value={requests.data ? waiting.length : undefined} detail={waiting.length ? 'People asking for more' : 'Nothing waiting'} needsYou={waiting.length > 0} />
      </div>
      <ListGroup
        title="Approved connections"
        count={connections?.length}
        description="People can use the projects listed on each. Removing a connection takes them away."
        empty={
          connections && connections.length === 0 ? (
            <Empty
              tone={TONE}
              icon={<Cloud />}
              title="No connections approved yet"
              body="Add the Azure directory or Azure Local cluster people may work with, and the projects in it. Until then nobody can use Foundry."
              action={
                mayEdit ? (
                  <Button size="sm" variant="primary" onPress={() => setForm({})}>
                    Add connection
                  </Button>
                ) : undefined
              }
            />
          ) : undefined
        }
      >
        {!connections && !list.error ? <ListRow title="Reading the connections…" /> : null}
        {(connections ?? []).map((c) => {
          const local = c.kind === 'azure-local';
          const names = (c.projects ?? []).map((p) => p.name);
          return (
            <ListRow
              key={c.id}
              icon={local ? <Server /> : <Cloud />}
              title={c.name}
              description={
                <>
                  <span className="block">{local ? `${KIND_WORDS[c.kind]} · cluster ${c.azureLocal?.cluster}${c.azureLocal?.disconnected ? ' · works disconnected' : ''}` : `${KIND_WORDS[c.kind]} · directory ${c.tenantId}`}</span>
                  <span className="block">
                    {local ? 'One cluster' : plural(projectCount(c), 'project')} · {runsAs(c.identity)}
                    {c.organisationSecret ? ` with the secret ${c.organisationSecret}` : ''}
                  </span>
                  {names.length ? <span className="block truncate">{names.join(', ')}</span> : null}
                </>
              }
              compactControl
              control={
                mayEdit ? (
                  <RowMenu
                    label={`More for ${c.name}`}
                    items={[
                      { id: 'edit', label: 'Edit…' },
                      { id: 'remove', label: 'Remove…', danger: true },
                    ]}
                    onAction={(id) => (id === 'edit' ? setForm({ connection: c }) : setRemoving(c))}
                  />
                ) : undefined
              }
            >
              {local ? (
                <p className="text-xs text-muted">
                  This cluster was added to <Link className="text-accent hover:underline" to="/assurance">Providers and Models</Link> unrated, so its work is treated as “Exploration only” until it is rated. Record an assessment there; “Sovereign-ready” fits when the hardware is the organisation’s own, in the country.
                </p>
              ) : null}
            </ListRow>
          );
        })}
      </ListGroup>

      {form ? (
        <ConnectionForm
          connection={form.connection}
          onClose={() => setForm(undefined)}
          onSaved={() => {
            setForm(undefined);
            list.reload();
          }}
        />
      ) : null}
      <ConfirmDialog
        isOpen={Boolean(removing)}
        title={`Remove ${removing?.name ?? ''}?`}
        confirmLabel="Remove"
        danger
        onCancel={() => setRemoving(undefined)}
        onConfirm={async () => {
          const c = removing!;
          setRemoving(undefined);
          setError(undefined);
          try {
            await foundryRoutes.removeConnection(c.id);
            list.reload();
          } catch (e) {
            setError(problemText(e));
          }
        }}
      >
        People lose the {removing?.kind === 'azure-local' ? 'cluster' : 'projects'} this connection approved, and the organisation’s Foundry policy is rewritten without {removing?.kind === 'azure-local' ? 'it' : 'them'}.
      </ConfirmDialog>
    </PageColumn>
  );
}
